import {
  CcfBox,
  CcfButton,
  CcfTextField,
  CcfTypography,
  useTranslator,
} from '@nice-ccf/ui-controls';
import styles from '../ccf-consumer-card-notes-tab.module.scss';
import { makeStyles } from '@mui/styles';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab';
import { useMemo } from 'react';
import { notesHistory } from '../mock-data/notes-history-mock-data';
import CcfNotesCard from '../ccf-notes-card/ccf-notes-card';
import { Box, InputAdornment, Popover } from '@mui/material';

/* eslint-disable-next-line */
export interface CcfNotesTabListViewProps {
  notesHistoryData: [];
  notesViewSelectHandler: any;
  editNote: any;
  updatedNote: any;
  setUpdatedNote: any;
  newNote: any;
  setNewNote: any;
  handlePopoverClose: any;
  noteAnchorEl: any;
  openNewNote: any;
  addNewNote: any;
}

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiTimelineItem-missingOppositeContent:before': {
      flex: 'none',
      padding: 0,
    },
    '& .MuiTimeline-root': {
      margin: 0,
      padding: '10px',
    },
    '& .MuiTimelineItem-root': {
      minHeight: 'auto',
    },
  },
  lastTimelineItem: {
    minHeight: 'auto',
  },

  titleInput: {
    '& .MuiOutlinedInput-root': {
      height: '55px', // Set your desired height here
    },
  },
  contentInput: {
    '& .MuiOutlinedInput-root': {
      height: '65px', // Set your desired height here
    },
  },
}));

/**@example */
export function CcfNotesTabListView(props: CcfNotesTabListViewProps) {
  const {
    notesHistoryData,
    notesViewSelectHandler,
    editNote,
    updatedNote,
    setUpdatedNote,
    newNote,
    setNewNote,
    handlePopoverClose,
    noteAnchorEl,
    openNewNote,
    addNewNote,
  } = props;
  const classes = useStyles();
  const [translate] = useTranslator();

  const groupInteractionsByDate = useMemo(() => {
    /**
     * Function to group the interactions by Date
     * @example groupInteractionsByDate()
     */
    const groupInteractionsByDateHandler = (notes: any) => {
      return notes.reduce((acc: any, note: any) => {
        const date = new Date(note.date).toLocaleDateString();
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(note);
        return acc;
      }, {});
    };

    const groupedData = groupInteractionsByDateHandler(notesHistoryData);
    const sortedKeys = Object.keys(groupedData).sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime()
    );
    const sortedGroupedData: Record<string, notesHistory[]> = {};
    sortedKeys.forEach((key) => {
      sortedGroupedData[key] = groupedData[key];
    });
    return sortedGroupedData;
  }, [notesHistoryData]);

  /**@example */
  const handleNotesView = (note: any) => {
    notesViewSelectHandler(note);
  };

  /**@example */
  const handleNewNote = (key: any, value: any) => {
    setNewNote({ ...newNote, [key]: value });
  };
  return (
    <>
      <CcfBox
        style={{ height: '90%', overflowY: 'auto' }}
        className={classes.root}
      >
        <Timeline>
          {Object.keys(groupInteractionsByDate).map(
            (date: string, index: number) => {
              const group = groupInteractionsByDate[date];

              return group.length > 1 ? (
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent>
                    <CcfTypography className={styles.timelineDate}>
                      {date}
                    </CcfTypography>
                    {group.map((note: any) => (
                      <CcfNotesCard
                        key={note.notesId}
                        note={note}
                        handleNotesView={handleNotesView}
                        editNote={editNote}
                        updatedNote={updatedNote}
                        setUpdatedNote={setUpdatedNote}
                      />
                    ))}
                  </TimelineContent>
                </TimelineItem>
              ) : (
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent>
                    <CcfTypography className={styles.timelineDate}>
                      {date}
                    </CcfTypography>
                    <CcfNotesCard
                      key={group[0].notesId}
                      note={group[0]}
                      handleNotesView={handleNotesView}
                      editNote={editNote}
                      updatedNote={updatedNote}
                      setUpdatedNote={setUpdatedNote}
                    />
                  </TimelineContent>
                </TimelineItem>
              );
            }
          )}
        </Timeline>
      </CcfBox>
      <Popover
        id="notesTab-addNote-popover"
        open={openNewNote}
        keepMounted
        anchorEl={noteAnchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <CcfBox className={styles.popoverContainer}>
          <CcfBox className={styles.popoverContent}>
            <CcfBox className={styles.timestampBlock}>
              <CcfBox>
                <CcfTypography
                  translationKey="addNotes"
                  className={styles.newNotetitle}
                ></CcfTypography>
              </CcfBox>
              <CcfBox className={styles.popovertextContent}>
                <CcfTextField
                  id="newNoteTitle"
                  size="small"
                  style={{
                    background: 'white',
                  }}
                  className={classes.titleInput}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box display="flex" flexDirection="column" gap="5px">
                          <CcfTypography
                            translationKey="note_title"
                            className={styles.newNotetitle}
                          ></CcfTypography>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  value={newNote.title}
                  onChange={(e: any) => {
                    handleNewNote('title', e.target.value);
                  }}
                  variant="outlined"
                  fullWidth
                />
                <textarea
                  rows={5}
                  id={`notesTab_newNoteContent`}
                  cols={21}
                  className={styles.textAreaInput}
                  value={newNote.content}
                  onChange={(e: any) =>
                    handleNewNote('content', e.target.value)
                  }
                />
              </CcfBox>
            </CcfBox>
          </CcfBox>
          <CcfBox
            style={{ display: 'flex', justifyContent: 'flex-end', gap: '5px' }}
          >
            <CcfButton
              onClick={() => {
                handlePopoverClose();
              }}
              style={{ height: '22px' }}
            >
              {translate('cancel')}
            </CcfButton>
            <CcfButton
              onClick={addNewNote}
              disabled={
                newNote &&
                Object.values(newNote).some(
                  (value) => value === '' || value == undefined
                )
              }
              primary
              style={{ height: '22px' }}
            >
              {translate('save')}
            </CcfButton>
          </CcfBox>
        </CcfBox>
      </Popover>
    </>
  );
}

export default CcfNotesTabListView;
